import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import { updateConfig } from "../../../../store/slices/Printing/printingSlice";
import {
  useGetPrintingTableDataQuery,
  useGetMultiGliListsTableDataQuery,
  useSubmitGliDataMutation,
} from "../../../../store/queries/Printing";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Style from "../printing.module.scss";
import { toast } from "react-toastify";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom";
import { PrintCertficate, PreviewPrintCertficate } from "../GLIPrint/api";
import { getPreviousPrintRecords } from "./api";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import domtoimage from "dom-to-image-more";
import {
  getPaperConfig,
  getPrintItemLimit,
} from "../CertificateTypes/VisitingCardOne/paperSize";
import html2pdf from "html2pdf.js";
import moment from "moment/moment";

const PREVIOUS_PRINT_RENDER_DELAY = 1200;
const IMAGE_EXPORT_SCALE = 4;

const sanitizeFileName = (value, fallback = "certificate") => {
  const cleanedValue = (value || fallback).toString().trim();

  return cleanedValue.replace(/[\\/:*?"<>|]+/g, "_").replace(/\s+/g, "_");
};

const waitForRender = (delay = PREVIOUS_PRINT_RENDER_DELAY) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });

// html2canvas-based capture — matches the PDF output exactly because html2pdf
// also uses html2canvas internally. Use this for card types where borders and
// pixel-exact layout matter (e.g. foldable card sections).
const captureNodeAsJpegHtml2Canvas = async (node) => {
  const canvas = await html2canvas(node, {
    useCORS: true,
    allowTaint: true,
    scale: IMAGE_EXPORT_SCALE,
    scrollY: 0,
    logging: false,
  });
  return canvas.toDataURL("image/jpeg", 1);
};

const captureNodeAsJpeg = async (node) => {
  // Temporarily remove overflow:hidden and fixed height constraints on the
  // node itself so dom-to-image captures the full content without clipping.
  const savedStyles = [];

  const saveAndPatch = (el, patches) => {
    const saved = { el };
    for (const prop of Object.keys(patches)) {
      saved[prop] = el.style[prop];
      el.style[prop] = patches[prop];
    }
    savedStyles.push(saved);
  };

  if (node.style.overflow === "hidden") {
    saveAndPatch(node, {
      overflow: "visible",
      maxHeight: "none",
      height: "auto",
    });
  }

  // Remove borders so they don't show as gray outlines when the
  // section is captured as a standalone image.
  node.querySelectorAll("*").forEach((el) => {
    if (el.style.border) {
      saveAndPatch(el, { border: "none" });
    }
  });

  const width = node.scrollWidth;
  const height = node.scrollHeight;

  try {
    return await domtoimage.toJpeg(node, {
      cacheBust: true,
      quality: 1,
      width: width * IMAGE_EXPORT_SCALE,
      height: height * IMAGE_EXPORT_SCALE,
      style: {
        transform: `scale(${IMAGE_EXPORT_SCALE})`,
        transformOrigin: "top left",
        width: `${width}px`,
        height: `${height}px`,
      },
      useCORS: true,
    });
  } finally {
    savedStyles.forEach((saved) => {
      const el = saved.el;
      for (const prop of Object.keys(saved)) {
        if (prop !== "el") el.style[prop] = saved[prop];
      }
    });
  }
};

const extractSummaryNoFromElement = (node, fallback = "certificate") => {
  const summaryNoElement = Array.from(node?.querySelectorAll("p") || []).find(
    (paragraph) => paragraph.textContent.trim() === "SUMMARY NO",
  );

  const summaryNoValue =
    summaryNoElement &&
    summaryNoElement.parentElement
      ?.querySelectorAll("p")?.[2]
      ?.textContent?.trim();

  return sanitizeFileName(summaryNoValue, fallback);
};

const downloadZipFile = async (zip, fileName) => {
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, fileName);
};

const usePrintingTable = ({
  visitingOneFrontPrintRef,
  visitingOneBackPrintRef,
  papperFrontPrintRef,
  papperBackPrintRef,
  bigFrontPrintRef,
  foldableCardRef,
  vistingDuplexRef,
  papperDuplexRef,
}) => {
  let paginationOptions = [
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
    { label: "80 Per page", value: 80 },
    { label: "120 Per page", value: 120 },
  ];
  const [loading, setLoading] = useState(false);
  const [previousPrintLoading, setPreviousPrintLoading] = useState(false);
  // Multi-GLI Card-Certificate item-count limit error (same inline message as PrintType).
  const [printLimitError, setPrintLimitError] = useState("");
  const dispatch = useDispatch();
  const state = useSelector((state) => state.printing);

  // Refs/state to drive the "preview before print" flow for multi GLI print.
  const pdfObjRef = useRef(null);
  const filenameRef = useRef("");
  const submittedDataRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    show: false,
    blobUrl: "",
    title: "",
  });

  const showPdfPreview = (pdfObj, title, filename) => {
    pdfObjRef.current = pdfObj;
    filenameRef.current = filename || "";
    const blobUrl = pdfObj.output("bloburl");
    setPreviewState({ show: true, blobUrl, title });
    setLoading(false);
  };

  const handleConfirmPrint = () => {
    // Only finalize the certificate creation/download on the server once the
    // user confirms via "Print & Upload".
    if (submittedDataRef.current) {
      PrintCertficate(submittedDataRef.current);
    }
    const pdfObj = pdfObjRef.current;
    if (pdfObj) {
      pdfObj.autoPrint();
      window.open(pdfObj.output("bloburl"), "F");
      if (filenameRef.current) {
        pdfObj.save(filenameRef.current);
      }
    }
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    submittedDataRef.current = null;
    handleCloseModal();
  };

  const handleCancelPreview = () => {
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    submittedDataRef.current = null;
    setLoading(false);
  };
  const { tableFields, globalRemarkModal } = useSelector(
    (state) => state.global,
  );
  const slug = sessionStorage?.getItem("slug");
  const rawGliData = state?.gliform?.gli_data;

  const transformedGliData =
    rawGliData?.map((statusValue) => ({
      gli_number: statusValue.gli_number,
      receipt_id: statusValue._id, // renaming _id to receipt_id
      status: statusValue.status,
    })) || [];
  const {
    data: tableData,
    isFetching,
    isLoading,
  } = useGetPrintingTableDataQuery({
    receipt_id: state?.selectedId,
    search: state.search,
    page_size: state.currentPageSize,
    page: state.currentPage,
    process_slug: slug,
    // work_status : [1,2,3,4,5]
  });

  const [submitGliData] = useSubmitGliDataMutation();

  const getCurrentTableRows = () =>
    state?.is_multi_print
      ? (state?.multipleTableAllData ?? [])
      : (tableData?.data?.data ?? []);

  const resetRenderedPrintingState = () => {
    dispatch(
      updateConfig((currentState) => {
        currentState.printingData = [];
        currentState.printUI = false;
      }),
    );
  };

  const updateRenderedPrintingData = (printingData, cardSize = 1) => {
    dispatch(
      updateConfig((currentState) => {
        currentState.printingData = printingData;
        currentState.printUI = true;
        currentState.card_size = cardSize;
      }),
    );
  };

  const buildZipName = (certificateNumbers, prefix = "previous_print") => {
    const normalizedNumbers = certificateNumbers
      .filter(Boolean)
      .map((item) => sanitizeFileName(item));
    const primaryName = normalizedNumbers[0] || prefix;
    const suffix =
      normalizedNumbers.length > 1
        ? `_plus_${normalizedNumbers.length - 1}`
        : "";

    return `${primaryName}${suffix}_${moment(Date.now()).format(
      "MM_DD_YYYY_HH_mm_ss",
    )}.zip`;
  };

  // Returns the list of capture plans for a given certificate type + side.
  // Each plan describes one ref/selector to capture from. Duplex (side=3)
  // produces two plans (front + back) so each certificate gets two JPGs.
  const getPreviousPrintPlans = (type, side) => {
    if (type === 1) {
      if (side === 2) {
        return [
          // {
          //   suffix: "",
          //   getRoot: () => visitingOneBackPrintRef?.current,
          //   selector: ".item-class-back",
          // },
        ];
      }
      if (side === 3) {
        return [
          {
            suffix: "",
            getRoot: () => visitingOneFrontPrintRef?.current,
            selector: ".item-class",
          },
          // {
          //   suffix: "",
          //   getRoot: () => visitingOneBackPrintRef?.current,
          //   selector: ".item-class-back",
          // },
        ];
      }
      return [
        {
          suffix: "",
          getRoot: () => visitingOneFrontPrintRef?.current,
          selector: ".item-class",
        },
      ];
    }

    if (type === 2) {
      return [
        {
          suffix: "",
          getRoot: () => bigFrontPrintRef?.current,
          selector: ".item-class-big-card",
        },
      ];
    }

    if (type === 3) {
      if (side === 2) {
        return [
          // {
          //   suffix: "",
          //   getRoot: () => papperBackPrintRef?.current,
          //   selector: "#element-to-print",
          // },
        ];
      }
      if (side === 3) {
        return [
          {
            suffix: "",
            getRoot: () => papperFrontPrintRef?.current,
            selector: ".item-class-paper",
          },
          // {
          //   suffix: "",
          //   getRoot: () => papperBackPrintRef?.current,
          //   selector: "#element-to-print",
          // },
        ];
      }
      return [
        {
          suffix: "",
          getRoot: () => papperFrontPrintRef?.current,
          selector: ".item-class-paper",
        },
      ];
    }

    if (type === 4) {
      return [
        // {
        //   suffix: "",
        //   getRoot: () => foldableCardRef?.current,
        //   selector: ".foldable-section-front",
        //   captureMethod: "html2canvas",
        // },
        {
          suffix: "",
          getRoot: () => foldableCardRef?.current,
          selector: ".foldable-section-back",
          captureMethod: "html2canvas",
        },
      ];
    }

    return [];
  };

  const normalizePreviousPrintRecords = (records = []) =>
    records
      .filter((record) => record?.response_data && record?.certificate_type)
      .map((record, index) => ({
        ...record.response_data,
        previous_print_type: record.certificate_type,
        previous_print_card_size: record.card_size ?? 1,
        previous_print_side: record.side ?? 1,
        printed_by: record.printed_by,
        previous_print_index: index,
      }));

  // const {
  //   data: MultiGLItableData,
  //   isFetching: isFetchingMultiGLI,
  //   isLoading: isLoadingMultiGLI,
  // } = useGetMultiGliListsTableDataQuery({
  //   gli_data: transformedGliData,
  //   search: state.search,
  //   page_size: state.currentPageSize,
  //   page: state.currentPage,
  //   process_slug: slug,
  // });
  // console.log(MultiGLItableData,transformedGliData,"MultiGLItableData")

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = tableData?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [tableData]);

  const handleRemarksClick = (data) => {
    dispatch(
      globalUpdateConfig((state) => {
        state.globalRemarkId = data?.id;
        state.globalRemarkModal = true;
      }),
    );
  };

  const handleCopyText = async (data) => {
    try {
      await navigator.clipboard.writeText(data);
      toast.success("Copied");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
      certificate_number: (feild, data) => (
        <p className="pro-mb-0 pro-d-flex pro-justify-between pro-items-center pro-gap-4">
          <span>{data[feild]}</span>
          <OverlayTrigger
            placement="auto"
            onToggle={(toggle) => {
              // const scrollWidth = window.innerWidth - document.documentElement.clientWidth;
              const body = document.body;
              body.style.overflow = toggle ? "hidden" : null;
              // body.style.paddingRight = toggle ? `${scrollWidth}px` : null;
            }}
            overlay={
              <Tooltip id="button-tooltip" className={Style.tooltip}>
                <table className="">
                  <tbody>
                    <tr>
                      <td>Summary No.</td>
                      <td> : </td>
                      <td>{data[feild]}</td>
                    </tr>
                    {data["graded_values"]?.qc_data?.map(
                      (item, index) =>
                        !!item.name && (
                          <tr key={index}>
                            <td>{item?.name}</td>
                            <td> : </td>
                            <td>{item?.value || "-"}</td>
                          </tr>
                        ),
                    )}
                  </tbody>
                </table>
              </Tooltip>
            }
          >
            <span className={Style.info_btn}>
              <span className="material-symbols-outlined pro-fw-medium">
                {" "}
                info{" "}
              </span>
            </span>
          </OverlayTrigger>
        </p>
      ),
      status: (_, data) =>
        data?.status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
      work_status: (_, data) =>
        data?.work_status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.work_status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.work_status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.work_status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.work_status}
          </span>
        ),
      action: (_, data) => (
        <p className="pro-mb-0">
          {
            <Button
              className={"pro-btn-primary"}
              onClick={() => handleRemarksClick(data)}
            >
              Remarks
            </Button>
          }
        </p>
      ),
      url: (field, data) =>
        data?.[field] ? (
          <div className="pro-d-flex pro-items-center pro-gap-2 no-wrap">
            <p className="pro-mb-0">{data[field]}</p>
            <span
              className={Style.copy_text}
              onClick={() => handleCopyText(data[field])}
            >
              <span className="material-symbols-outlined">content_copy</span>
            </span>
            <span
              className={Style.copy_text}
              onClick={() => handleDownload(data)}
            >
              <span className="material-symbols-outlined">download</span>
            </span>
          </div>
        ) : (
          ""
        ),
    };
    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleDownload = async (data) => {
    // Create a QR code container dynamically
    try {
      // Create a hidden container to render the QR code
      const qrCodeContainer = document.createElement("div");
      qrCodeContainer.style.display = "inline-block"; // Make sure it renders inline
      document.body.appendChild(qrCodeContainer);

      // Use ReactDOM to render the QRCode component inside the container
      ReactDOM.render(<QRCode value={data?.url} size={150} />, qrCodeContainer);

      //   const now = new Date();
      // const formattedDate = now
      //   .toISOString()
      //   .replace(/T/, "_") // Replace 'T' with an underscore
      //   .replace(/:/g, "-") // Replace colons with hyphens
      //   .split(".")[0]; // Remove the milliseconds

      //   const fileName = `qr_code_${formattedDate}.jpeg`;

      // Wait for QR code to render, then capture it with html2canvas
      const canvas = await html2canvas(qrCodeContainer, { scale: 2 });
      const qrCodeImage = canvas.toDataURL("image/jpeg");
      // Create a download link for the QR code image
      const downloadLink = document.createElement("a");
      downloadLink.href = qrCodeImage;

      downloadLink.download = `${data?.certificate_number}.jpeg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up by removing the QR code container from the DOM
      document.body.removeChild(qrCodeContainer);
    } catch (error) {
      console.error("Failed to download QR code:", error);
      toast.error("Failed to download QR code");
    }
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
    if (state?.is_multi_print) {
      const body = {
        gli_data: state?.transformedGliDataOrder,
        search: state.search,
        page_size: page_size,
        page: state?.page,
        process_slug: slug,
      };
      submitGliData(body).then((res) => {
        if (res?.data.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              const combinedData = [
                ...state.multipleTableAllData,
                ...res.data.data.data,
              ];
              const deduplicatedData = Array.from(
                new Map(combinedData.map((item) => [item.id, item])).values(),
              );
              state.multipleTableAllData = deduplicatedData;
              state.currentPage = state?.page;
              state.multipleTableData = res.data.data;
            }),
          );
        }
      });
    }
  };
  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
    if (state?.is_multi_print) {
      // dispatch(
      //   updateConfig((state) => {
      //     state.clear = true;
      //   })
      // );
      // const timeOut = setTimeout(() => {
      //   dispatch(updateConfig((state) => (state.clear = false)));
      //   clearTimeout(timeOut);
      // }, 1000);
      const body = {
        gli_data: state?.transformedGliDataOrder,
        search: state.search,
        page_size: state.currentPageSize,
        page: page,
        process_slug: slug,
      };
      submitGliData(body).then((res) => {
        if (res?.data.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              const combinedData = [
                ...state?.multipleTableAllData,
                ...res.data.data.data,
              ];
              const deduplicatedData = Array.from(
                new Map(combinedData.map((item) => [item.id, item])).values(),
              );
              state.multipleTableAllData = deduplicatedData;
              state.currentPage = page;
              state.multipleTableData = res.data.data;
            }),
          );
        }
      });
    }
  };

  const handleMultiPrintWithoutModal = (items) => {
    setLoading(true);
    const filteredData = (
      state?.is_multi_print
        ? state?.multipleTableAllData
        : tableData?.data?.data
    )?.filter((item) => items?.includes(item.id));

    const gliData =
      filteredData?.map((item) => {
        const matchedGLIData = state?.gliform?.gli_data?.find(
          (gliItem) => gliItem?.gli_number === item.gli_number,
        );

        return {
          gli_number: item.gli_number,
          receipt_id: item.receipt_id,
          report_id: matchedGLIData?.report_id ?? "",
          certificate_id: [item.id],
        };
      }) ?? [];

    // Payload used to actually create/print once the user confirms the preview.
    const formattedData = {
      certificate_type: state?.gliform?.certificate_type?._id ?? "",
      gli_data: gliData,
      remarks: state?.gliform?.remarks ?? "",
    };
    submittedDataRef.current = formattedData;

    // Payload for the preview-print endpoint.
    const previewData = {
      certificate_type: state?.gliform?.certificate_type?._id ?? "",
      certificate_format: state?.gliform?.certificate_format?.id ?? 1,
      gli_data: gliData.map(
        ({ gli_number, receipt_id, report_id, certificate_id }) => ({
          gli_number,
          receipt_id,
          report_id,
          certificate_id,
        }),
      ),
    };

    PreviewPrintCertficate(previewData)
      .then((response) => {
        if (response?.data?.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              state.printingData = response?.data?.data;
              state.printUI = true;
              state.clear = true;
            }),
          );
          const timmerId = setTimeout(() => {
            dispatch(updateConfig((state) => (state.clear = false)));
            clearTimeout(timmerId);
          }, 100);
          if (state?.gliform?.certificate_format?.id === 2) {
            // Image format has no interactive PDF preview, so finalize the
            // certificate creation immediately.
            PrintCertficate(formattedData);
            submittedDataRef.current = null;
            setLoading(false);
            handleImagePrints(state?.gliform);
          } else {
            handlePDFPrints(state?.gliform);
          }
        } else if (response?.status_code === 422) {
          setLoading(false);
          toast.error("Something went wrong");
          dispatch(
            updateConfig((state) => {
              state.clear = true;
            }),
          );
        } else {
          setLoading(false);
          dispatch(
            updateConfig((state) => {
              state.clear = true;
            }),
          );
          toast.error("No data available.");
        }
      })
      .catch((err) => {
        // Backstop so an unexpected failure never leaves the button stuck at
        // "Printing...".
        console.error("Multi-print preview failed", err);
        setLoading(false);
        toast.error("Something went wrong");
        dispatch(
          updateConfig((state) => {
            state.clear = true;
          }),
        );
      });
  };

  const handlePrintClick = (items) => {
    if (state.is_multi_print) {
      // Card-Certificate item-count guard for the multi-GLI batch. Same inline message as
      // PrintType; blocks the print and shows the error when the selection exceeds the cap.
      const certTypeId = state?.gliform?.certificate_type?._id;
      const sideId = state?.gliform?.side?.id;
      const limit =
        certTypeId === 1 && sideId
          ? getPrintItemLimit(state?.paper_size, sideId)
          : null;
      if (limit != null && (items?.length ?? 0) > limit) {
        setPrintLimitError(
          `Maximum ${limit} items allowed for ${
            state?.gliform?.paper_size?.name ?? ""
          } ${sideId === 3 ? "duplex" : "front/back"} — you have selected ${
            items?.length ?? 0
          }.`,
        );
        return;
      }
      setPrintLimitError("");
      handleMultiPrintWithoutModal(items);
      dispatch(
        updateConfig((state) => {
          state.selectedItems = items;
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.printModal = true;
          state.selectedItems = items;
        }),
      );
    }
  };

  const handleModalClose = () => {
    dispatch(globalUpdateConfig((state) => (state.globalRemarkModal = false)));
  };

  const handleSearch = (e) => {
    dispatch(updateConfig((state) => (state.search = e?.target?.value)));
    if (state?.is_multi_print) {
      const body = {
        gli_data: state?.transformedGliDataOrder,
        search: e?.target?.value,
        page_size: state.currentPageSize,
        page: state?.page,
        process_slug: slug,
      };
      submitGliData(body).then((res) => {
        if (res?.data.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              const combinedData = [
                ...state?.multipleTableAllData,
                ...res.data.data.data,
              ];
              const deduplicatedData = Array.from(
                new Map(combinedData.map((item) => [item.id, item])).values(),
              );
              state.multipleTableAllData = deduplicatedData;
              state.currentPage = state?.page;
              state.multipleTableData = res.data.data;
            }),
          );
        }
      });
    }
  };

  const addNodeImageToZip = async (
    zip,
    node,
    fileName,
    captureFn = captureNodeAsJpeg,
  ) => {
    const imageData = await captureFn(node);

    if (!imageData) {
      return false;
    }

    zip.file(`${sanitizeFileName(fileName)}.JPG`, imageData.split(",")[1], {
      base64: true,
    });

    return true;
  };

  const exportImageNodesAsZip = async ({
    rootElement,
    elements,
    zipName,
    fallbackName,
    onComplete,
  }) => {
    if (!rootElement) {
      toast.error("No elements in the DOM");
      return;
    }

    if (!elements.length) {
      toast.error("No certificate elements found to export.");
      return;
    }

    const zip = new JSZip();
    let generatedCount = 0;

    for (const element of elements) {
      const fileName = extractSummaryNoFromElement(element, fallbackName);
      const created = await addNodeImageToZip(zip, element, fileName);
      if (created) {
        generatedCount += 1;
      }
    }

    if (!generatedCount) {
      toast.error("No images generated");
      return;
    }

    await downloadZipFile(zip, zipName);

    if (typeof onComplete === "function") {
      onComplete();
    }
  };

  function handleImagePrints(values) {
    if (values?.certificate_type?._id === 1) {
      if (values?.side?.id === 1) {
        handleVisitingCardImage(values);
      }
    } else if (values?.certificate_type?._id === 2) {
      handleBigCardImage();
    }
  }

  function handleBigCardImage() {
    setTimeout(async () => {
      const element = bigFrontPrintRef?.current;
      const items = Array.from(
        element?.querySelectorAll(".item-class-big-card") ?? [],
      );

      try {
        await exportImageNodesAsZip({
          rootElement: element,
          elements: items,
          zipName: `big_cards_images_${moment(Date.now()).format(
            "MM_DD_YYYY_HH_mm_ss",
          )}.zip`,
          fallbackName: "big_card",
          onComplete: handleCloseModal,
        });
      } catch (error) {
        console.error("Error exporting big card images", error);
        toast.error("Failed to export big card images");
      }
    }, 1000);
  }

  function handleVisitingCardImage() {
    setTimeout(async () => {
      const element = visitingOneFrontPrintRef?.current;
      const items = Array.from(element?.querySelectorAll(".item-class") ?? []);

      try {
        await exportImageNodesAsZip({
          rootElement: element,
          elements: items,
          zipName: `visiting_cards_images_${moment(Date.now()).format(
            "MM_DD_YYYY_HH_mm_ss",
          )}.zip`,
          fallbackName: "visiting_card",
          onComplete: handleCloseModal,
        });
      } catch (error) {
        console.error("Error exporting visiting card images", error);
        toast.error("Failed to export visiting card images");
      }
    }, 1000);
  }

  // Guarded html2pdf pipeline for the "preview before print" flow. Bails out
  // with a toast (and releases the loading state) when the print ref is empty
  // or PDF generation fails, so the button never gets stuck at "Printing...".
  const printPdfFromRef = (ref, options, title = "Preview") => {
    const element = ref?.current;
    if (!element) {
      toast.error("No elements in the DOM");
      setLoading(false);
      return;
    }
    html2pdf()
      .set(options)
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, title, options?.filename);
      })
      .catch(function (err) {
        console.error("PDF preview generation failed", err);
        toast.error("Failed to generate PDF preview.");
        setLoading(false);
      });
  };

  function handlePDFPrints(values) {
    if (values?.certificate_type?._id === 1) {
      if (values?.side?.id === 1) {
        handleVisitingTypeOneFrontPrintClick();
      } else if (values?.side?.id === 2) {
        handleVisitingTypeOneBackPrintClick();
      } else {
        handleVisitingDuplexPrint();
      }
    }
    if (values?.certificate_type?._id === 2) {
      handleBigClick();
    }
    if (values?.certificate_type?._id === 3) {
      if (values?.side?.id === 1) {
        handlePaperFrontClick();
      } else if (values?.side?.id === 2) {
        handlePaperBackClick();
      } else {
        handlePaperDuplexPrint();
      }
    }
    if (values?.certificate_type?._id === 4) {
      handleFoldableCardPrint();
    }
    const timeOutId = setTimeout(() => {
      dispatch(
        updateConfig((state) => {
          state.printingData = [];
          state.printUI = false;
        }),
      );
      clearTimeout(timeOutId);
    }, 1500);
  }

  const handleVisitingTypeOneFrontPrintClick = () => {
    const paper = getPaperConfig(state?.paper_size);
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}visiting card certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      visitingOneFrontPrintRef,
      {
        margin: [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: {
          unit: "mm",
          format: paper.format,
          orientation: paper.orientation,
        },
        pagebreak: false,
        DisablePdfCompression: true,
      },
      "Visiting Card Certificate - Front",
    );
  };

  const handleVisitingTypeOneBackPrintClick = () => {
    const paper = getPaperConfig(state?.paper_size);
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state.gli_id + "_" : ""}visiting card certificate back_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      visitingOneBackPrintRef,
      {
        margin: [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: {
          unit: "mm",
          format: paper.format,
          orientation: paper.orientation,
        },
        pagebreak: false,
        DisablePdfCompression: true,
      },
      "Visiting Card Certificate - Back",
    );
  };

  const handlePaperFrontClick = () => {
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}paper certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      papperFrontPrintRef,
      {
        margin: [0, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        // pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        DisablePdfCompression: true,
      },
      "Paper Certificate - Front",
    );
  };

  const handlePaperBackClick = () => {
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}paper certificate back_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      papperBackPrintRef,
      {
        margin: [5, 5, 5, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        DisablePdfCompression: true,
      },
      "Paper Certificate - Back",
    );
  };

  const handleBigClick = () => {
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}big card certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      bigFrontPrintRef,
      {
        margin: [5, 5, 5, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        DisablePdfCompression: true,
      },
      "Big Card Certificate - Front",
    );
  };

  const handleVisitingDuplexPrint = () => {
    const paper = getPaperConfig(state?.paper_size);
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}visiting card duplex_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      vistingDuplexRef,
      {
        margin: paper.margin ?? [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: {
          unit: "mm",
          format: paper.format,
          orientation: paper.orientation,
        },
        // pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      },
      "Visiting Card Duplex",
    );
  };

  const handlePaperDuplexPrint = () => {
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}paper certificate duplex_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      papperDuplexRef,
      {
        margin: [0, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      },
      "Paper Certificate Duplex",
    );
  };

  const handleFoldableCardPrint = () => {
    const filename = `${state?.is_multi_print || state?.isMultiPrint ? "" : state?.gli_id ? state?.gli_id + "_" : ""}foldable card_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    printPdfFromRef(
      foldableCardRef,
      {
        margin: [12, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a5", orientation: "landscape" },
        DisablePdfCompression: true,
      },
      "Foldable Card",
    );
  };

  const exportPreviousPrintZip = async (
    records,
    certificateNumbers,
    progressToastId,
  ) => {
    // Group by (type, side, card_size) because each group needs a separate
    // render pass with its own card_size and a side-specific capture plan.
    const groupedRecords = records.reduce((accumulator, record) => {
      const type = record?.previous_print_type;
      const side = record?.previous_print_side ?? 1;
      const cardSize = record?.previous_print_card_size ?? 1;
      const key = `${type}__${side}__${cardSize}`;

      if (!accumulator[key]) {
        accumulator[key] = { type, side, cardSize, records: [] };
      }

      accumulator[key].records.push(record);
      return accumulator;
    }, {});

    // Count total capture tasks for progress tracking
    let totalCaptures = 0;
    for (const group of Object.values(groupedRecords)) {
      const plans = getPreviousPrintPlans(group.type, group.side);
      totalCaptures += group.records.length * plans.length;
    }

    const zip = new JSZip();
    let generatedCount = 0;
    let processedCount = 0;

    const updateProgress = () => {
      const progress = totalCaptures > 0 ? processedCount / totalCaptures : 0;
      toast.update(progressToastId, {
        render: `Processing ${processedCount} of ${totalCaptures} images...`,
        progress,
      });
    };

    try {
      for (const group of Object.values(groupedRecords)) {
        const { type, side, cardSize, records: typeRecords } = group;
        const plans = getPreviousPrintPlans(type, side);

        if (!plans.length) {
          toast.error(`Previous print is not configured for type ${type}.`);
          continue;
        }

        updateRenderedPrintingData(typeRecords, cardSize);
        await waitForRender();

        for (const plan of plans) {
          const rootElement = plan.getRoot();

          if (!rootElement) {
            toast.error(
              `Previous print is not configured for type ${type} / side ${side}.`,
            );
            continue;
          }

          const captureTargets = Array.from(
            rootElement.querySelectorAll(plan.selector),
          );

          if (!captureTargets.length) {
            toast.error(
              `No printable certificate found for type ${type} / side ${side}.`,
            );
            continue;
          }

          for (let index = 0; index < typeRecords.length; index += 1) {
            const record = typeRecords[index];
            const captureTarget = captureTargets[index];

            if (!captureTarget) {
              processedCount += 1;
              updateProgress();
              continue;
            }

            const baseName = sanitizeFileName(
              record?.certificate_number || record?.gli_number,
              `certificate_${type}_${index + 1}`,
            );
            const fileName = `${baseName}${plan.suffix}`;

            const captureFn =
              plan.captureMethod === "html2canvas"
                ? captureNodeAsJpegHtml2Canvas
                : captureNodeAsJpeg;
            const created = await addNodeImageToZip(
              zip,
              captureTarget,
              fileName,
              captureFn,
            );
            if (created) {
              generatedCount += 1;
            }
            processedCount += 1;
            updateProgress();
          }
        }
      }

      if (!generatedCount) {
        toast.update(progressToastId, {
          render: "No previous print certificates were generated.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      toast.update(progressToastId, {
        render: "Creating ZIP file...",
        progress: 0.95,
      });

      await downloadZipFile(zip, buildZipName(certificateNumbers));

      toast.update(progressToastId, {
        render: `Download complete! ${generatedCount} image${generatedCount > 1 ? "s" : ""} exported.`,
        type: "success",
        isLoading: false,
        autoClose: 4000,
        progress: undefined,
      });
    } finally {
      resetRenderedPrintingState();
    }
  };

  const handleCloseModal = () => {
    dispatch(
      updateConfig((state) => {
        state.printModal = false;
      }),
    );
    // setIsLoading(false);
  };

  const handlePreviousPrint = async (selectedIds = []) => {
    if (previousPrintLoading) {
      toast.warn("Previous print export is already in progress.");
      return;
    }

    if (!selectedIds?.length) {
      toast.warn("Select at least one certificate to continue.");
      return;
    }

    const selectedRows = getCurrentTableRows().filter((item) =>
      selectedIds.includes(item.id),
    );

    const certificateNumbers = selectedRows
      .map((item) => item?.certificate_number)
      .filter(Boolean);

    if (!certificateNumbers.length) {
      toast.error("Selected rows do not contain certificate numbers.");
      return;
    }

    setPreviousPrintLoading(true);

    const progressToastId = toast.loading("Fetching print records...", {
      progress: 0,
    });

    try {
      const response = await getPreviousPrintRecords({
        certificate_number: certificateNumbers,
      });
      const responsePayload = response?.data ?? response;

      if (responsePayload?.status_code !== 200) {
        toast.update(progressToastId, {
          render:
            responsePayload?.message ||
            "Failed to fetch previous print records.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      const normalizedRecords = normalizePreviousPrintRecords(
        responsePayload?.data ?? [],
      );

      if (!normalizedRecords.length) {
        toast.update(progressToastId, {
          render: "No previous print records found.",
          type: "error",
          isLoading: false,
          autoClose: 4000,
          progress: undefined,
        });
        return;
      }

      toast.update(progressToastId, {
        render: "Preparing images...",
        progress: 0.05,
      });

      await exportPreviousPrintZip(
        normalizedRecords,
        certificateNumbers,
        progressToastId,
      );
    } catch (error) {
      console.error("Previous print export failed", error);
      toast.update(progressToastId, {
        render: "Failed to export previous print certificates.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
        progress: undefined,
      });
      resetRenderedPrintingState();
    } finally {
      setPreviousPrintLoading(false);
    }
  };
  return {
    loading,
    previousPrintLoading,
    isFetching,
    isLoading,
    tableData,
    tableFields,
    globalRemarkModal,
    paginationOptions,
    state,
    currentPageSize: state.currentPageSize,
    getRow,
    handlePageSize,
    handleModalClose,
    handlePagination,
    handlePrintClick,
    handleSearch,
    handlePreviousPrint,
    printLimitError,
    previewState,
    handleConfirmPrint,
    handleCancelPreview,
  };
};

export default usePrintingTable;
